import React, { useEffect, useState, useCallback } from 'react';
import { Wrench, ChevronDown, ChevronUp, RefreshCw, Shield } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation, formatCount } from '@/i18n';
import { apiClient, ToolAnalyticsResponse } from '@/utils/apiClient';

interface ToolFrequencyPanelProps {
  className?: string;
}

export const ToolFrequencyPanel: React.FC<ToolFrequencyPanelProps> = ({ className }) => {
  const { t, language } = useTranslation();
  const counts = t.analytics.counts;
  const at = (t as any).analytics;
  const [data, setData] = useState<ToolAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getToolAnalytics(days);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : (at?.fetchError ?? 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [days, at?.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">{at?.loading ?? 'Loading...'}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-500 dark:text-red-400">{error}</span>
          <button onClick={fetchData} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {at?.refresh ?? 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const topTools = data.top_tools.slice(0, 10);
  const blockRate = data.total_tool_calls > 0
    ? ((data.blocked_duplicates / data.total_tool_calls) * 100).toFixed(1)
    : '0.0';

  return (
    <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700', className)}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <Wrench className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {at?.toolFrequency ?? 'Tool Frequency'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {at?.topPrefix ?? 'Top'} {topTools.length} · {at?.blockRate ?? 'Block Rate'}: {blockRate}%
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="flex items-center gap-1">
              <Shield className="h-3 w-3 text-green-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                {formatCount(language, data.blocked_duplicates, counts.blockedDuplicates)}
              </span>
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-4">
          {/* Period selector */}
          <div className="flex gap-2">
            {[7, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={clsx(
                  'px-2.5 py-1 text-xs rounded-md transition-colors',
                  days === d
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                )}
              >
                {d === 7 ? (at?.days7 ?? '7 days') : (at?.days30 ?? '30 days')}
              </button>
            ))}
          </div>

          {topTools.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">
              {at?.noToolCalls ?? 'No tool calls recorded yet'}
            </p>
          ) : (
            <ul className="space-y-2">
              {topTools.map((tool, idx) => {
                const maxCount = topTools[0]?.count || 1;
                const pct = (tool.count / maxCount) * 100;
                return (
                  <li key={tool.name} className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums w-4 text-right flex-shrink-0">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono text-gray-700 dark:text-gray-300 truncate">
                          {tool.name}
                        </span>
                        <span className="text-gray-400 dark:text-gray-500 tabular-nums ml-2 flex-shrink-0">
                          {formatCount(language, tool.count, counts.calls)} · {tool.avg_duration_ms.toFixed(0)} {at?.ms ?? 'ms'}
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mt-1">
                        <div
                          className="h-full bg-amber-400 dark:bg-amber-500 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
